import {
  ImageScaleView,
  ImageScaleViewOpts,
  View,
  animate,
} from '@play-co/timestep-core/ui';
import bitmapFonts from 'src/lib/bitmapFonts';
import { captureGenericError } from 'src/lib/sentry';
import LangBitmapFontTextView, {
  LangBitmapOpts,
} from 'src/lib/ui/components/LangBitmapFontTextView';
import { animDuration, getApplication } from 'src/lib/utils';

type AnimateHandler = () => void;
type AnimationOpts = {
  hide?: (view: View) => void;
  show?: (view: View) => void;
};

/**
 * Show text and images on top of the game
 */
export class TutorialOverlayService {
  private parent: View;
  private showAnimations: AnimateHandler[] = [];
  private hideAnimations: AnimateHandler[] = [];

  constructor() {
    this.parent = new View({
      tag: 'tutorial-overlay',
      width: 720,
      height: 1280,
      visible: false,
      canHandleEvents: false,
      zIndex: 10002,
    });
  }

  /**
   * Get superview for the overlay
   */
  private getSuperview() {
    // Create parent view.
    let superview = new View();

    // Ignore errors if we cannot get the root view.
    try {
      superview = getApplication().getRootView();
    } catch (e) {
      console.error(e);
      captureGenericError('Cannot get root view for tutorial hand service', e);
    }

    return superview;
  }

  /**
   * Clear all views previously added
   */
  public clear() {
    this.parent.removeAllSubviews();
    this.showAnimations = [];
    this.hideAnimations = [];
  }

  /**
   * Add text view to the overlay
   */
  public async addText(
    text: string,
    style?: LangBitmapOpts,
    on?: AnimationOpts,
  ) {
    const view = new LangBitmapFontTextView({
      superview: this.parent,
      x: this.parent.style.width / 2,
      y: this.parent.style.height / 2,
      centerOnOrigin: true,
      font: bitmapFonts('Title'),
      localeText: () => text,
      wordWrap: true,
      align: 'center',
      ...style,
    });

    if (on && on.show) {
      this.showAnimations.push(() => on.show && on.show(view));
    }

    if (on && on.hide) {
      this.hideAnimations.push(() => on.hide && on.hide(view));
    }
  }

  /**
   * Add image view to the overlay
   */
  public async addImage(
    image: string,
    style?: ImageScaleViewOpts,
    on?: AnimationOpts,
  ) {
    const view = new ImageScaleView({
      superview: this.parent,
      image,
      x: this.parent.style.width / 2,
      y: this.parent.style.height / 2,
      centerOnOrigin: true,
      ...style,
    });

    if (on && on.show) {
      this.showAnimations.push(() => on.show && on.show(view));
    }

    if (on && on.hide) {
      this.hideAnimations.push(() => on.hide && on.hide(view));
    }
  }

  /**
   * Show overlay
   */
  public async show() {
    this.parent.style.opacity = 0;
    this.parent.show();
    this.getSuperview().addSubview(this.parent);

    this.showAnimations.forEach((anim) => anim());

    return animate(this.parent)
      .then({ opacity: 1 }, animDuration * 2)
      .toPromise();
  }

  /**
   * Hide overlay
   */
  public hide() {
    this.hideAnimations.forEach((anim) => anim());

    return animate(this.parent)
      .then({ opacity: 0 }, animDuration)
      .then(() => {
        this.parent.hide();
        this.clear();
        this.parent.removeFromSuperview();
      })
      .toPromise();
  }
}
